import { chevronRight } from "../../assets/icons/SvgIconList";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { CURRENCY_SIGN } from "../../utils/AppConstants";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { PRICE_CHECK_STATUS_ENUM } from "../../utils/PurchaseEnumerations";
import { formatAmountWithSign } from "../../utils/PurchaseFormat";
import {
  checkPrice,
  formatPaise,
  formatRupees,
  INearestPrice,
  ITaxRatesInput,
} from "../../utils/PriceCalculator";

interface IPriceBoxProps {
  isError: boolean;
  children: React.ReactNode;
}

interface IPriceBreakdownProps {
  basePrice: string | number | null;
  rates: ITaxRatesInput;
  allowZeroTotal?: boolean;
}

const PriceBox: React.FC<IPriceBoxProps> = ({ isError, children }) => (
  <div
    className={cn("rounded-md p-3 text-sm space-y-1", {
      "bg-danger-muted text-danger": isError,
      "bg-base": !isError,
    })}
  >
    {children}
  </div>
);

const PriceBreakdown: React.FC<IPriceBreakdownProps> = ({
  basePrice,
  rates,
  allowZeroTotal = false,
}) => {
  const { t } = UseTranslationHook();
  const check = checkPrice(basePrice, rates);

  if (check.status === PRICE_CHECK_STATUS_ENUM.Empty) return null;

  if (check.status === PRICE_CHECK_STATUS_ENUM.InvalidAmount) {
    return (
      <PriceBox isError>
        <div>{t("price_invalid_amount")}</div>
      </PriceBox>
    );
  }
  if (check.status === PRICE_CHECK_STATUS_ENUM.InvalidRates) {
    return (
      <PriceBox isError>
        <div>{t("price_invalid_rates")}</div>
      </PriceBox>
    );
  }

  if (check.status === PRICE_CHECK_STATUS_ENUM.Valid) {
    const { breakdown } = check;
    return (
      <PriceBox isError={check.below_minimum && !allowZeroTotal}>
        <div>
          {prepareMessageFromParams(t("price_live_same_state"), [
            ["currency", CURRENCY_SIGN],
            ["base", formatPaise(breakdown.base_paise)],
            ["cgst_rate", String(rates.cgst)],
            ["cgst", formatPaise(breakdown.cgst_paise)],
            ["sgst_rate", String(rates.sgst)],
            ["sgst", formatPaise(breakdown.sgst_paise)],
          ])}{" "}
          <b>{formatAmountWithSign(breakdown.total_paise / 100)}</b>
        </div>
        <div>
          {prepareMessageFromParams(t("price_live_other_state"), [
            ["currency", CURRENCY_SIGN],
            ["base", formatPaise(breakdown.base_paise)],
            ["igst_rate", String(rates.igst)],
            ["igst", formatPaise(breakdown.igst_paise)],
          ])}{" "}
          <b>{formatAmountWithSign(breakdown.total_paise / 100)}</b>
        </div>
        {check.below_minimum && !allowZeroTotal && (
          <div className="text-danger">{prepareMessageFromParams(t("price_minimum_total"), [["currency", CURRENCY_SIGN]])}</div>
        )}
      </PriceBox>
    );
  }

  const nearestLine = (option: INearestPrice) => (
    <div className="flex items-start gap-1">
      <span className="size-4 shrink-0 mt-0.5">{chevronRight}</span>
      <span>
        {prepareMessageFromParams(t("price_nearest_item"), [
          ["currency", CURRENCY_SIGN],
          ["price", formatPaise(option.base_paise)],
          ["cgst", formatPaise(option.breakdown.cgst_paise)],
          ["sgst", formatPaise(option.breakdown.sgst_paise)],
          ["total", formatPaise(option.breakdown.total_paise)],
        ])}
      </span>
    </div>
  );

  return (
    <PriceBox isError>
      <div className="font-semibold">
        {prepareMessageFromParams(t("price_cant_use"), [
          ["currency", CURRENCY_SIGN],
          ["price", formatRupees(basePrice)],
        ])}
      </div>
      {check.exact_taxes.map((tax) => (
        <div key={tax.label}>
          {prepareMessageFromParams(t("price_tax_line"), [
            ["currency", CURRENCY_SIGN],
            ["tax", tax.label],
            ["rate", tax.rate],
            ["price", formatRupees(basePrice)],
            ["exact", tax.exact],
          ])}
        </div>
      ))}
      <div className="pt-1">
        {prepareMessageFromParams(t("price_too_many_decimals"), [
          ["currency", CURRENCY_SIGN],
          ["exact", check.first_inexact.exact],
          ["decimals", String(check.first_inexact.decimals)],
        ])}
      </div>
      <div className="pt-1">{t("price_nearest")}</div>
      {check.below && nearestLine(check.below)}
      {nearestLine(check.above)}
    </PriceBox>
  );
};

export default PriceBreakdown;
